import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Hammer, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../utils/formatters';
import { input, label } from '../../styles/components';

export function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setIsSending(true);
    const result = await resetPassword(token, password);
    setIsSending(false);
    if (result.success) {
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } else {
      setError(result.error || 'No se pudo restablecer la contraseña');
    }
  };

  if (!token) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-3">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-[var(--b2b-accent)]">
              <Hammer className="w-7 h-7 text-white" />
            </span>
            <span className="text-2xl font-extrabold tracking-tight">ReformAll</span>
          </Link>
        </div>
        <div className="b2b-panel p-8 text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-[var(--b2b-surface-muted)]">
            <AlertCircle className="w-8 h-8 text-[color:var(--b2b-danger)]" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight">Enlace no válido</h1>
          <p className="b2b-muted">Falta el token de restablecimiento. Solicita un enlace nuevo.</p>
          <Link to="/forgot-password" className="b2b-btn b2b-btn--primary w-full justify-center">
            Solicitar nuevo enlace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-[var(--b2b-accent)]">
            <Hammer className="w-7 h-7 text-white" />
          </span>
          <span className="text-2xl font-extrabold tracking-tight">ReformAll</span>
        </Link>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Nueva contraseña</h1>
          <p className="b2b-muted">Define una nueva contraseña para tu cuenta</p>
        </div>
      </div>

      <div className="b2b-panel p-8">
        {done ? (
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-[var(--b2b-surface-muted)]">
              <CheckCircle className="w-8 h-8 text-[color:var(--b2b-accent-strong)]" />
            </div>
            <h2 className="text-xl font-bold">Contraseña restablecida</h2>
            <p className="b2b-muted">Ya puedes iniciar sesión con tu nueva contraseña. Redirigiendo...</p>
            <button onClick={() => navigate('/login')} className="b2b-btn b2b-btn--primary w-full justify-center">
              Ir al login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label className={label} htmlFor="password">Nueva contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={cn(input, 'pl-9 pr-9')}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 b2b-muted"
                  aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className={label} htmlFor="confirm">Confirmar contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className={cn(input, 'pl-9')}
                  placeholder="Repite la contraseña"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {error && <p className="text-sm text-[color:var(--b2b-danger)]">{error}</p>}

            <button type="submit" disabled={isSending} className="b2b-btn b2b-btn--primary w-full justify-center disabled:opacity-60">
              {isSending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </span>
              ) : (
                'Restablecer contraseña'
              )}
            </button>

            <p className="text-center text-sm b2b-muted">
              <Link to="/login" className="font-medium text-[color:var(--b2b-accent-strong)] hover:underline">
                Volver al login
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
